import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { analyzeFoot, normalizeAnalysis, ensureToken } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import EnhancedAnalysisResult from '../components/EnhancedAnalysisResult';
import AssistantPanel from '../components/AssistantPanel';
import DemographicsFields from '../components/DemographicsFields';
import ClinicalContextFields from '../components/ClinicalContextFields';

const WOUND_TYPES = [
  { name: 'Neuropathic', desc: 'Pressure / callus driven — painless; plantar surface', color: 'text-amber-300' },
  { name: 'Ischemic', desc: 'Poor blood supply — painful; toes / lateral margin', color: 'text-red-300' },
  { name: 'Infected', desc: 'Erythema, warmth, purulent discharge — urgent care', color: 'text-red-400' },
  { name: 'Neuroischaemic', desc: 'Mixed aetiology — most common in T2DM', color: 'text-orange-300' },
];

const PHOTO_TIPS = [
  'Good natural or bright indoor lighting — avoid flash glare',
  'Place foot on a flat, plain background (white sheet works well)',
  'Capture top, sole, and both sides in separate shots if needed',
  'Include a ruler or common object for scale when possible',
  'Remove dressings / bandages before photographing the wound',
];

const FootAnalysis = () => {
  const { currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeCondition, setActiveCondition] = useState(null);
  const [demographics, setDemographics] = useState({});
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setError(null);
    if (!selectedFile) { setFile(null); setPreview(null); return; }
    if (!selectedFile.type.startsWith('image/')) {
      setError('Please select an image file'); setFile(null); setPreview(null); return;
    }
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer.files?.[0]) handleFileChange({ target: { files: e.dataTransfer.files } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select a foot photograph to analyse'); return; }
    setIsAnalyzing(true); setResult(null); setError(null);
    try {
      const token = await ensureToken(currentUser?.token);
      const resp = await analyzeFoot(file, token, demographics);
      if (resp.model_status !== 'ready') {
        setError('The foot AI model is unavailable — ensure GEMINI_API_KEY is configured in the backend .env.');
        return;
      }
      const mapped = normalizeAnalysis(resp);
      setResult(mapped);
      setActiveCondition(mapped.conditions[0]?.name || null);
    } catch (err) {
      setError(err.message || 'An error occurred during analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFile(null); setPreview(null); setResult(null);
    setError(null); setActiveCondition(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-10 page-transition">
        <div className="container-custom">
          {/* Header */}
          <div className="flex items-center mb-8">
            <Link to="/analysis" className="text-amber-400 hover:text-amber-300 mr-4 flex items-center">
              <svg className="h-5 w-5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="ml-2">Back to Analyses</span>
            </Link>
            <div>
              <h1 className="heading-secondary">Diabetic Foot Analysis</h1>
              <p className="text-amber-400 text-sm mt-0.5">Wound Classification · Powered by Gemini Vision AI</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Upload panel */}
            <div className="bg-gray-900 rounded-xl shadow-md p-6 border border-gray-800">
              <h2 className="heading-subsection mb-4">Upload Foot Photograph</h2>

              {error && (
                <div className="bg-red-900/30 border-l-4 border-red-500 p-4 mb-4 text-red-400">
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div
                  className="border-2 border-dashed border-gray-700 hover:border-amber-500 transition-colors duration-300 rounded-lg p-8 text-center cursor-pointer"
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {preview ? (
                    <img src={preview} alt="Foot preview" className="mx-auto max-h-64 object-contain rounded" />
                  ) : (
                    <div className="mb-4">
                      <div className="mx-auto h-24 w-24 flex items-center justify-center bg-gray-800 rounded-full mb-4">
                        <svg className="h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                      </div>
                      <p className="text-gray-400 mt-2">Drag and drop a foot photograph, or click to select</p>
                      <p className="text-gray-500 text-sm mt-1">JPEG / PNG · Clinical photography or smartphone camera</p>
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                </div>

                {/* Photo tips */}
                <div className="bg-amber-900/20 border-l-4 border-amber-600 p-4">
                  <p className="text-sm font-medium text-amber-300 mb-2">Photography tips for best results:</p>
                  <ul className="space-y-1">
                    {PHOTO_TIPS.map((tip, i) => (
                      <li key={i} className="text-xs text-amber-200/80 flex items-start gap-1.5">
                        <span className="mt-0.5 shrink-0">·</span>{tip}
                      </li>
                    ))}
                  </ul>
                </div>

                <DemographicsFields value={demographics} onChange={setDemographics} showDiabetesFields />
                <div className="mt-4">
                  <ClinicalContextFields value={demographics} onChange={setDemographics} />
                </div>

                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={handleReset}
                    className="px-4 py-2 border border-gray-700 rounded-md text-sm font-medium text-gray-300 hover:bg-gray-800 transition-colors">
                    Reset
                  </button>
                  <button type="submit" disabled={!file || isAnalyzing}
                    className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${
                      !file || isAnalyzing
                        ? 'bg-amber-900/40 cursor-not-allowed'
                        : 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700'
                    }`}>
                    {isAnalyzing ? 'Analysing...' : 'Analyse Foot Image'}
                  </button>
                </div>
              </form>
            </div>

            {/* Results panel */}
            <div className="bg-gray-900 rounded-xl shadow-md p-6 border border-gray-800">
              <h2 className="heading-subsection mb-4">Classification Result</h2>

              {isAnalyzing ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <div className="relative h-20 w-20">
                    <div className="absolute inset-0 rounded-full border-t-2 border-b-2 border-amber-500 animate-spin" />
                    <div className="absolute inset-2 rounded-full border-r-2 border-l-2 border-orange-500 animate-spin animation-delay-150" />
                    <div className="absolute inset-4 rounded-full border-t-2 border-b-2 border-yellow-400 animate-spin animation-delay-300" />
                  </div>
                  <p className="mt-6 text-gray-300">Classifying foot wound...</p>
                  <p className="mt-2 text-gray-500 text-sm">Gemini Vision is analysing the image</p>
                </div>
              ) : result ? (
                <div className="space-y-6">
                  <EnhancedAnalysisResult
                    image={preview}
                    conditions={result.conditions}
                    activeCondition={activeCondition}
                    onConditionClick={(c) => setActiveCondition(c.name)}
                    findings={result.findings}
                    confidence={result.confidence}
                    recommendation={result.recommendation}
                  />
                  <AssistantPanel
                    analysisId={result.id}
                    assistant={result.assistant}
                    assistantName={result.assistantName}
                    disclaimer={result.disclaimer}
                    severity={result.conditions[0]?.severity}
                    risk={result.conditions[0]?.severity === 'Severe' ? 'malignant' : null}
                    chatContext={{
                      modality: 'foot',
                      top_finding: result.conditions[0]?.name,
                      severity: result.conditions[0]?.severity,
                      age: demographics.age,
                      sex: demographics.sex,
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <div className="bg-gray-800 h-24 w-24 rounded-full flex items-center justify-center mb-4">
                    <svg className="h-12 w-12 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="text-gray-400">Upload a foot photograph to classify wound type</p>
                </div>
              )}
            </div>
          </div>

          {/* Wound type reference */}
          <div className="mt-8 bg-gray-900 rounded-xl p-6 border border-gray-800">
            <h2 className="heading-subsection mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Diabetic Foot Ulcer Classification
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
              {WOUND_TYPES.map((w) => (
                <div key={w.name} className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                  <div className={`font-semibold text-sm mb-1 ${w.color}`}>{w.name}</div>
                  <div className="text-xs text-gray-400">{w.desc}</div>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-sm">
              Powered by <span className="text-amber-400 font-mono">Gemini 1.5 Flash</span> vision model.
              All classifications require confirmation by a podiatrist, vascular surgeon, or diabetologist.
              Infected or ischemic ulcers are medical emergencies — do not delay referral.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FootAnalysis;
